import { STAGGER, VARIANTS, staggerParent } from "@/lib/motion";
import { motion, type HTMLMotionProps } from "motion/react";
import type { ReactNode } from "react";

type StaggerTag = "div" | "ul" | "ol" | "li" | "section";

/** Props of `StaggerGroup`; `step` names a `STAGGER` gap, `delay` holds the first child back (seconds). */
export type StaggerGroupProps = Omit<HTMLMotionProps<"div">, "variants" | "initial" | "animate" | "children"> & {
  as?: Exclude<StaggerTag, "li">;
  step?: keyof typeof STAGGER;
  delay?: number;
  children?: ReactNode;
};

/** Reveals its `StaggerItem` children one after another on mount (heroes, result lists). */
export function StaggerGroup({ as = "div", step = "base", delay = 0, ...rest }: StaggerGroupProps) {
  const Tag = motion[as] as typeof motion.div;
  return <Tag {...rest} variants={staggerParent(STAGGER[step], delay)} initial="hidden" animate="visible" />;
}

/** Props of `StaggerItem`. */
export type StaggerItemProps = Omit<HTMLMotionProps<"div">, "variants" | "children"> & {
  as?: StaggerTag;
  children?: ReactNode;
};

/** Child of `StaggerGroup`: fades up when its turn comes. */
export function StaggerItem({ as = "div", ...rest }: StaggerItemProps) {
  const Tag = motion[as] as typeof motion.div;
  return <Tag {...rest} variants={VARIANTS.fadeUp} />;
}
